import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Store, select } from "@ngrx/store";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { AppState } from "../../app.reducer";
import { selectIsLoggedIn } from "./auth.selectors";

/**
 * Inverse of AuthGuard: blocks access to public-only routes (login,
 * onboarding, forgot-password) when the user is already authenticated.
 *
 * Without this guard, typing `/login` into the address bar while logged
 * in shows the login form and lets the user re-authenticate — confusing
 * and a vector for accidental session-overwrite bugs. With it, those
 * routes redirect to `/` (or to the `returnUrl` query param if present,
 * so deep-link flows still work).
 */
@Injectable({
  providedIn: "root",
})
export class RedirectIfAuthenticatedGuard implements CanActivate {
  constructor(
    private router: Router,
    private store: Store<AppState>,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | boolean
    | UrlTree
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree> {
    return this.store.pipe(
      select(selectIsLoggedIn),
      take(1),
      map((isLoggedIn) => {
        if (!isLoggedIn) {
          return true;
        }
        const returnUrl = route.queryParamMap.get("returnUrl");
        if (returnUrl && returnUrl !== "/login") {
          return this.router.parseUrl(returnUrl);
        }
        return this.router.createUrlTree(["/"]);
      }),
    );
  }
}
